import { useState, useEffect } from "react";
import Spreadsheet from "react-spreadsheet";
import VesselHeader from "../../components/Vessel/VesselHeader";
import { getVesselsList } from "../../services/vesselOverviewService";
import { useDispatch, useSelector } from "react-redux";
import {getCIIData} from '../../services/complianceService';
import BenchmarkYearPicker from "../../components/Common/BenchmarkYearPicker";
import ExportToExcel from "../../components/Common/ExportToExcel";

const CII = () => {

   const dispatch = useDispatch();
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasRealData, setHasRealData] = useState(false);

  // Static data as fallback
  const staticData = [
    [
      {
        value: "Vessel Name",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "IMO",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "Capacity in CII calculation",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "Deadweight/ Gross Tonnage (mt)",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "Distance Sailed (nm)",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "Distance deducted (nm)",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "CO2 emitted (mt)",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "CO2 emission deducted (mt)",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      // {
      //   value: "CII Trend (3 days-avg)",
      //   readOnly: true,
      //   className: "font-bold bg-blue-50 text-blue-800",
      // },
      {
        value: "Attained",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "Required",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "Deviation in %",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      {
        value: "Attained Rating YTD",
        readOnly: true,
        className: "font-bold bg-blue-50 text-blue-800",
      },
      // {
      //   value: "Projected Rating next year",
      //   readOnly: true,
      //   className: "font-bold bg-blue-50 text-blue-800",
      // },
      // {
      //   value: "Projected year of deterioration",
      //   readOnly: true,
      //   className: "font-bold bg-blue-50 text-blue-800",
      // },
    ],
    // Static fallback row
    [
      { value: "No Data", readOnly: true, className: "font-medium" },
      { value: "N/A", readOnly: true, className: "text-center" },
      { value: 0, readOnly: true, className: "text-right" },
      { value: 0, readOnly: true, className: "text-right" },
      { value: 0, readOnly: true, className: "text-right" },
      { value: 0, readOnly: true, className: "text-right" },
      { value: 0, readOnly: true, className: "text-right" },
      { value: 0, readOnly: true, className: "text-right" },
      { value: "N/A", readOnly: true, className: "text-center" },
      { value: 0, readOnly: true, className: "text-right" },
      { value: 0, readOnly: true, className: "text-right" },
      { value: "0%", readOnly: true, className: "text-right" },
      { value: "N/A", readOnly: true, className: "text-center" },
      { value: "N/A", readOnly: true, className: "text-center" },
      { value: "N/A", readOnly: true, className: "text-center" },
    ]
  ];

  const benchmarkYear = useSelector((state) => state.benchmarkYear.year);

  useEffect(() => {
    const fetchCIIData = async () => {
      setLoading(true);
      
      if (!currentVessel?._id) {
        console.log("No current vessel selected, using static data");
        setData(staticData);
        setLoading(false);
        return;
      }

      try {
        const response = await getCIIData(currentVessel._id, benchmarkYear);
        console.log("CII API Response:", response);

        if (response?.success && response.data && response.data.length > 0) {
          const formattedData = formatCIIDataForSpreadsheet(response.data);
          setData(formattedData);
          setHasRealData(true);
        } else {
          setData(staticData);
          setHasRealData(false);
        }
      } catch (err) {
        console.error("Failed to fetch CII data:", err);
        setData(staticData);
        setHasRealData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchCIIData();
  }, [currentVessel, benchmarkYear]);

  const formatCIIDataForSpreadsheet = (ciiData) => {
    const headerRow = staticData[0];

    const ciiRows = ciiData.map((vessel) => {
      const co2Emitted = vessel.fuels?.reduce((total, fuel) => {
        const emissionFactor = getCO2EmissionFactor(fuel.fuelType);
        return total + (fuel.quantityMT * emissionFactor);
      }, 0) || 0;

      // const co2EmissionDeducted = vessel.fuels?.reduce((total, fuel) => {
      //   const emissionFactor = getCO2EmissionFactor(fuel.fuelType);
      //   const deduction = fuel.quantityMT * 0.1;
      //   return total + (deduction * emissionFactor);
      // }, 0) || 0;

      const trendValue = vessel.ciiTrend || 0;
      const trendDisplay = trendValue > 0 ? `${trendValue.toFixed(2)} (stable)` : "N/A";

      const getRatingClass = (rating) => {
        switch (rating) {
          case 'A': return 'text-green-600';
          case 'B': return 'text-blue-600';
          case 'C': return 'text-yellow-600';
          case 'D': return 'text-orange-600';
          case 'E': return 'text-red-600';
          default: return 'text-gray-600';
        }
      };

      return [
        { 
          value: vessel.vesselName || currentVessel?.name || "Unknown Vessel", 
          readOnly: true, 
          className: "font-medium" 
        },
        { 
          value: vessel.imoNumber || currentVessel?.imoNumber || "N/A", 
          readOnly: true, 
          className: "text-center" 
        },
        { 
          value: vessel.capacity || 0, 
          readOnly: true, 
          className: "text-right" 
        },
        { 
          value: vessel.deadweight || 0, 
          readOnly: true, 
          className: "text-right" 
        },
        { 
          value: vessel.distanceTravelled?.toFixed(1) || 0, 
          readOnly: true, 
          className: "text-right" 
        },
        { 
          value: vessel.distanceDeducted?.toFixed(1) || 0, 
          readOnly: true, 
          className: "text-right" 
        },
        { 
          value: vessel.co2Emitted.toFixed(1), 
          readOnly: true, 
          className: "text-right" 
        },
        { 
          value: vessel.co2EmissionDeducted.toFixed(1), 
          readOnly: true, 
          className: "text-right" 
        },
        // { 
        //   value: trendDisplay, 
        //   readOnly: true, 
        //   className: "text-center" 
        // },
        { 
          value: vessel.attainedCII?.toFixed(6) || 0, 
          readOnly: true, 
          className: "text-right" 
        },
        { 
          value: vessel.requiredCII?.toFixed(6) || 0, 
          readOnly: true, 
          className: "text-right" 
        },
        { 
          value: vessel.deviationPercent ? `${vessel.deviationPercent.toFixed(6)}%` : "0%", 
          readOnly: true, 
          className: "text-right" 
        },
        { 
          value: vessel.attainedRating || "N/A", 
          readOnly: true, 
          className: `text-center font-bold ${getRatingClass(vessel.attainedRating)}` 
        },
        // { 
        //   value: vessel.projectedRating || "N/A", 
        //   readOnly: true, 
        //   className: `text-center font-bold ${getRatingClass(vessel.projectedRating)}` 
        // },
        // { 
        //   value: vessel.projectedDeteriorationYear ? `E(${vessel.projectedDeteriorationYear})` : "N/A", 
        //   readOnly: true, 
        //   className: "text-center" 
        // },
      ];
    });

    return [headerRow, ...ciiRows];
  };

  const getCO2EmissionFactor = (fuelType) => {
    const factors = {
      'HFO': 3.114,
      'LFO': 3.151,
      'MGO': 3.206,
      'MDO': 3.206,
      'LNG': 2.750,
      'Biofuel': 0,
      'Ethane': 1.375,
      'Others': 3.114
    };
    return factors[fuelType] || 3.114;
  };

  useEffect(() => {
    const fetchAllVesselsCIIData = async () => {
      if (currentVessel?._id) {
        return;
      }

      try {
        const vesselData = await getVesselsList();
        console.log("All vessels data:", vesselData);

        if (vesselData && vesselData.length > 0) {
          const allVesselsCIIData = await Promise.all(
            vesselData.map(async (vessel) => {
              try {
                const ciiResponse = await getCIIData(vessel._id);
                if (ciiResponse?.success && ciiResponse.data && ciiResponse.data.length > 0) {
                  return ciiResponse.data[0];
                }
                return null;
              } catch (error) {
                console.error(`Failed to fetch CII data for vessel ${vessel.name}:`, error);
                return null;
              }
            })
          );

          const validCIIData = allVesselsCIIData.filter(data => data !== null);
          
          if (validCIIData.length > 0) {
            const formattedData = formatCIIDataForSpreadsheet(validCIIData);
            setData(formattedData);
            setHasRealData(true);
          } else {
            setData(staticData);
            setHasRealData(false);
          }
        } else {
          setData(staticData);
        }
      } catch (err) {
        console.error("Failed to fetch vessels list:", err);
        setData(staticData);
      }
    };

    if (!currentVessel?._id) {
      fetchAllVesselsCIIData();
    }
  }, [currentVessel]);

  return (
    <div className="font-sans text-sm min-h-screen bg-gray-50">
      <div className="flex items-center justify-between p-4 border-b border-gray-300">
        <VesselHeader pageTitle="CII Compliance Data" />
        <div className="flex space-x-2">
          <BenchmarkYearPicker />
          {hasRealData && <ExportToExcel />}
        </div>
      </div>

      <div className="p-4">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-x-auto flex items-center justify-center">
          {loading ? (
            <div className="text-gray-500 text-base animate-pulse">
              Loading data...
            </div>
          ) : (
            <div className="min-w-[1100px]">
              <Spreadsheet
                data={data}
                onChange={setData}
                hideRowIndicators={true}
                hideColumnIndicators={true}
                className="enhanced-spreadsheet"
              />
            </div>
          )}
        </div>
      </div>

      <style jsx global>{`
        .enhanced-spreadsheet {
          --selected-cell: #e3f2fd;
          --selected-row: #f5f5f5;
          --selected-column: #f5f5f5;
        }
        .enhanced-spreadsheet table {
          border-collapse: separate;
          border-spacing: 0;
          width: auto !important;
        }
        .enhanced-spreadsheet td {
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 10px 14px !important;
          transition: background-color 0.2s;
          white-space: nowrap;
          color: #1f2937 !important;
        }
        .enhanced-spreadsheet td,
        .enhanced-spreadsheet th {
          white-space: nowrap !important; /* No wrapping */
        }
        .enhanced-spreadsheet td:first-child {
          border-left: 1px solid #e5e7eb !important;
        }
        .enhanced-spreadsheet tr:first-child td {
          position: sticky;
          top: 0;
          background-color: #f8fafc !important;
          z-index: 5;
        }
        .enhanced-spreadsheet td:nth-child(10), /* Attained */
        .enhanced-spreadsheet td:nth-child(11), /* Required */
        .enhanced-spreadsheet td:nth-child(12)  /* Deviation in % */ {
          background-color: #fffbe6 !important;
          font-weight: bold;
        }
        .enhanced-spreadsheet tr:hover td {
          background-color: #f9fafb !important;
        }
        .enhanced-spreadsheet .active-cell {
          box-shadow: inset 0 0 0 2px #3b82f6 !important;
        }
      `}</style>
    </div>
  );
};

export default CII;
